import { useEffect, useState } from "react";
import { submitContactMessage } from "../../services/publicApi";
import { useSiteSettings } from "../../hooks/useSiteSettings";
import "./ContactPage.css";

const emptyContactForm = {
  name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

function updateMeta(title, description) {
  if (title) {
    document.title = title;
  }

  if (description) {
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.setAttribute("name", "description");
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute("content", description);
  }
}

function ContactPage() {
  const { settings, loading, error } = useSiteSettings();
  const [formData, setFormData] = useState(emptyContactForm);
  const [formError, setFormError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (settings) {
      updateMeta(settings.contact_meta_title, settings.contact_meta_description);
    }
  }, [settings]);

  function updateForm(changes) {
    setFormData((currentData) => ({ ...currentData, ...changes }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setFormError("");
    setSuccessMessage("");
    setSubmitting(true);

    try {
      const response = await submitContactMessage(formData);
      setSuccessMessage(response.detail || settings.contact_success_message);
      setFormData(emptyContactForm);
    } catch (requestError) {
      setFormError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <main className="section public-simple-page"><div className="container empty-state">Loading contact details...</div></main>;
  }

  if (error || !settings?.contact_page_enabled) {
    return (
      <main className="section public-simple-page">
        <div className="container-sm empty-state">
          <strong>That page could not be found.</strong>
          <span>Please head home or choose a location to browse the menu.</span>
        </div>
      </main>
    );
  }

  const contactMethods = [
    { label: "Phone", value: settings.contact_phone || settings.primary_phone, href: `tel:${settings.contact_phone || settings.primary_phone}` },
    { label: "Email", value: settings.contact_email || settings.primary_email, href: `mailto:${settings.contact_email || settings.primary_email}` },
    { label: "Address", value: settings.contact_address_text },
    { label: "Hours", value: settings.contact_hours_text },
  ].filter((method) => method.value);

  return (
    <main className="section public-contact-page">
      <div className="container public-contact-grid">
        <section>
          <p className="eyebrow">{settings.contact_eyebrow}</p>
          <h1>{settings.contact_title}</h1>
          <p className="public-page-intro">{settings.contact_intro}</p>

          <div className="public-contact-methods">
            {contactMethods.map((method) => (
              <article className="card" key={method.label}>
                <div className="card-body">
                  <strong>{method.label}</strong>
                  {method.href ? <a href={method.href}>{method.value}</a> : <p>{method.value}</p>}
                </div>
              </article>
            ))}
          </div>
        </section>

        {settings.contact_form_enabled ? (
          <form className="card public-contact-form" onSubmit={handleSubmit}>
            <div className="card-body flex flex-column gap-4">
              <h2>Send a message</h2>
              {formError ? <div className="alert alert-error">{formError}</div> : null}
              {successMessage ? <div className="alert alert-success">{successMessage}</div> : null}
              <input required placeholder="Name" value={formData.name} onChange={(event) => updateForm({ name: event.target.value })} />
              <input required type="email" placeholder="Email" value={formData.email} onChange={(event) => updateForm({ email: event.target.value })} />
              <input placeholder="Phone" value={formData.phone} onChange={(event) => updateForm({ phone: event.target.value })} />
              <input required placeholder="Subject" value={formData.subject} onChange={(event) => updateForm({ subject: event.target.value })} />
              <textarea required placeholder="Message" value={formData.message} onChange={(event) => updateForm({ message: event.target.value })} />
              <button className="btn btn-primary" disabled={submitting} type="submit">
                {submitting ? "Sending" : "Send Message"}
              </button>
            </div>
          </form>
        ) : null}
      </div>
    </main>
  );
}

export default ContactPage;